import {useEffect, useState} from 'react';
import {Control, FieldValues, Path, useWatch} from 'react-hook-form';

/**
 * Custom hook that debounces useWatch for better performance when watching frequently changing fields
 *
 * @param control - React Hook Form control
 * @param name - Field names to watch (array) or single field name
 * @param delay - Debounce delay in milliseconds (default: 150ms)
 * @returns Debounced values array or single value
 */
export function useDebouncedWatch<TFieldValues extends FieldValues = FieldValues>(
  control: Control<TFieldValues> | undefined,
  name: readonly Path<TFieldValues>[],
  delay?: number
): any[];

export function useDebouncedWatch<TFieldValues extends FieldValues = FieldValues>(
  control: Control<TFieldValues> | undefined,
  name: Path<TFieldValues>,
  delay?: number
): any;

export function useDebouncedWatch<TFieldValues extends FieldValues = FieldValues>(
  control: Control<TFieldValues> | undefined,
  name: readonly Path<TFieldValues>[] | Path<TFieldValues>,
  delay: number = 150
) {
  const watchedValue = useWatch({control, name: name as any});
  const [debouncedValue, setDebouncedValue] = useState(watchedValue);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(watchedValue);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [watchedValue, delay]);

  return debouncedValue;
}
